import { useMemo } from 'react';
import type { ReactNode } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { PlatformApiError, platformApiRequest } from '../api/client.ts';
import { BrandWordmark } from '../components/BrandWordmark.tsx';
import { useSession } from '../hooks/useSession.tsx';
import { LocaleSwitcher, useT } from '../i18n/index.ts';
import type { I18nKey } from '../i18n/index.ts';

/**
 * Tenant app chrome — sidebar + header + main area. Renders the
 * authenticated experience for every tenant-facing page under the
 * root route tree (see `App.tsx`).
 *
 * Visual contract: DESIGN.md §5 (authoritative copy lives in
 * `apps/ops-console/DESIGN.md`). 256px sidebar on `surface-container-low`,
 * 56px header on `surface-container-high`, `surface` main area with
 * `p-8` padding. Depth is expressed through background tone only — no
 * 1px section borders.
 *
 * This file mirrors the ops-console Shell almost 1:1; the only
 * differences are the nav surface + the logout endpoint + the "signed
 * in" account chip (tenant doesn't have roles, just an email and an
 * `emailVerified` flag).
 *
 * All user-visible text routes through `useT()` so the header language
 * switcher can flip the whole shell in place — no reload. The `testId`
 * slug on each nav entry is derived from the route path (not the label)
 * so tests stay stable across locale changes.
 */
interface NavItem {
  readonly to: string;
  readonly labelKey: I18nKey;
  readonly testId: string;
  readonly icon: string;
}

const NAV_ITEMS: readonly NavItem[] = [
  { to: '/dashboard', labelKey: 'shell.nav.dashboard', testId: 'dashboard', icon: 'dashboard' },
  { to: '/keys', labelKey: 'shell.nav.keys', testId: 'keys', icon: 'vpn_key' },
  { to: '/usage', labelKey: 'shell.nav.usage', testId: 'usage', icon: 'analytics' },
  { to: '/requests', labelKey: 'shell.nav.requests', testId: 'requests', icon: 'receipt_long' },
  { to: '/pricing', labelKey: 'shell.nav.pricing', testId: 'pricing', icon: 'sell' },
  { to: '/billing', labelKey: 'shell.nav.billing', testId: 'billing', icon: 'payments' },
  { to: '/settings', labelKey: 'shell.nav.settings', testId: 'settings', icon: 'settings' },
];

interface Props {
  readonly children?: ReactNode;
}

export function Shell({ children }: Props): JSX.Element {
  const { user, clearUser, fetchImpl } = useSession();
  const location = useLocation();
  const navigate = useNavigate();
  const t = useT();

  const pageTitleKey = useMemo<I18nKey>(() => {
    const match = NAV_ITEMS.find((item) => location.pathname.startsWith(item.to));
    return match?.labelKey ?? 'shell.page.console_fallback';
  }, [location.pathname]);

  async function handleLogout(): Promise<void> {
    try {
      await platformApiRequest<{ ok: boolean }>('/v1/auth/logout', { method: 'POST' }, fetchImpl);
    } catch (err) {
      // Even if /logout fails we clear the client-side mirror so the UI
      // doesn't stay in an inconsistent "logged in" state. The cookie
      // will expire on its own TTL regardless.
      if (!(err instanceof PlatformApiError)) {
        throw err;
      }
    }
    clearUser();
    navigate('/login', { replace: true });
  }

  const displayEmail =
    user?.email !== undefined && user.email.length > 0 ? user.email : t('shell.session.signed_in');
  const verifiedBadge =
    user?.emailVerified === true ? t('shell.session.verified') : t('shell.session.unverified');

  return (
    <div className="flex h-screen overflow-hidden bg-surface text-on-surface">
      <aside
        data-testid="shell-sidebar"
        className="flex min-h-0 w-64 shrink-0 flex-col bg-surface-container-low px-4 py-6"
      >
        <div className="mb-8 shrink-0 px-2">
          <BrandWordmark descriptor={t('shell.brand.subtitle')} testId="shell-brand-wordmark" />
          <p className="mt-4 text-xs leading-5 text-outline">{t('shell.brand.supporting')}</p>
        </div>
        <nav
          aria-label={t('shell.nav.aria_label')}
          className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto overscroll-contain"
        >
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`shell-nav-${item.testId}`}
              className={({ isActive }) =>
                [
                  'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-semibold tracking-tight transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface-container-low',
                  isActive
                    ? 'bg-surface-container text-primary'
                    : 'text-outline hover:bg-surface-container hover:text-on-surface',
                ].join(' ')
              }
            >
              <span aria-hidden="true" className="material-symbols-outlined text-[20px]">
                {item.icon}
              </span>
              <span>{t(item.labelKey)}</span>
            </NavLink>
          ))}
        </nav>
        <div className="mt-auto flex shrink-0 flex-col gap-3 pt-6">
          <div className="flex flex-col gap-1 rounded-lg bg-surface-container px-3 py-3">
            <span
              className="truncate text-xs font-semibold text-on-surface"
              data-testid="shell-email"
            >
              {displayEmail}
            </span>
            <span
              className="inline-flex w-fit rounded-full bg-surface-container-highest px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.1em] text-primary"
              data-testid="shell-verified"
            >
              {verifiedBadge}
            </span>
          </div>
          <button
            type="button"
            onClick={() => {
              void handleLogout();
            }}
            data-testid="shell-logout"
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-surface-container-highest px-3 py-2 text-xs font-bold uppercase tracking-[0.08em] text-on-surface transition-colors hover:bg-surface-bright focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface-container-low"
          >
            <span aria-hidden="true" className="material-symbols-outlined text-[18px]">
              logout
            </span>
            <span>{t('shell.logout')}</span>
          </button>
        </div>
      </aside>
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-10 flex h-14 shrink-0 items-center justify-between bg-surface-container-high px-8">
          <h1
            className="text-base font-semibold tracking-tight text-on-surface"
            data-testid="shell-page-title"
          >
            {t(pageTitleKey)}
          </h1>
          <div className="flex items-center gap-4">
            <LocaleSwitcher />
          </div>
        </header>
        <main className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-contain p-8">
          {children ?? <Outlet />}
        </main>
      </div>
    </div>
  );
}
